// src/context/CustomerContext.js
// The student's permanent customer number (e.g. EF-482913). Fetched once per
// session after sign-in via the support backend and cached in sessionStorage.
// Never blocks the UI: until it arrives (or if the call fails) `customer` is
// null and the screens simply leave the line out.
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { ensureCustomer } from '../services/supportService';

const CustomerContext = createContext({ customer: null });
const KEY = 'elpis_customer';

export function loadCachedCustomer(uid) {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    return v && v.uid === uid ? v.customer : null;
  } catch { return null; }
}

export function CustomerProvider({ children }) {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;
  const [customer, setCustomer] = useState(() => (uid ? loadCachedCustomer(uid) : null));

  useEffect(() => {
    if (!uid) { setCustomer(null); return undefined; }
    const cached = loadCachedCustomer(uid);
    if (cached) { setCustomer(cached); return undefined; }
    let cancelled = false;
    ensureCustomer()
      .then((c) => {
        if (cancelled || !c || !c.customerNumber) return;
        setCustomer(c);
        try { sessionStorage.setItem(KEY, JSON.stringify({ uid, customer: c })); } catch { /* storage blocked: refetched next load */ }
      })
      .catch(() => { /* backend not reachable: carry on without a number */ });
    return () => { cancelled = true; };
  }, [uid]);

  return <CustomerContext.Provider value={{ customer }}>{children}</CustomerContext.Provider>;
}

export function useCustomer() {
  return useContext(CustomerContext).customer;
}
